import { useState, useEffect } from "react";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Shield,
  Settings,
  Trash2,
  Eye,
  Loader2,
  Search,
  ChevronDown,
  Check,
  AlertTriangle,
  Building,
  User,
  Lock
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Alert,
  AlertDescription,
} from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API_URL = process.env.REACT_APP_API_URL;

const ROLE_DESCRIPTIONS = {
  operations_manager: {
    name: "Operations Manager",
    description: "Manage campaigns, calls, and agent operations",
    icon: Settings,
    color: "bg-blue-100 text-blue-600"
  },
  sales_manager: {
    name: "Sales Manager",
    description: "Manage leads, sales pipeline, and reports",
    icon: Users,
    color: "bg-green-100 text-green-600"
  },
  qa_manager: {
    name: "QA Manager",
    description: "Review calls, score quality, flag compliance",
    icon: Shield,
    color: "bg-purple-100 text-purple-600"
  },
  it_manager: {
    name: "IT Manager",
    description: "Manage technical settings and integrations",
    icon: Settings,
    color: "bg-amber-100 text-amber-600"
  },
  franchise_admin: {
    name: "Franchise Admin",
    description: "Territory oversight and revenue reports",
    icon: Building,
    color: "bg-rose-100 text-rose-600"
  },
  agent: {
    name: "Agent",
    description: "Basic user access - make calls, view assigned leads",
    icon: User,
    color: "bg-slate-100 text-slate-600"
  },
  readonly: {
    name: "Read Only",
    description: "View-only access to dashboards",
    icon: Eye,
    color: "bg-gray-100 text-gray-600"
  }
};

export default function AdminSubAdmins() {
  const { user } = useAdminBoard();
  const [loading, setLoading] = useState(true);
  const [subAdmins, setSubAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [selectedSubAdmin, setSelectedSubAdmin] = useState(null);
  const [effectivePermissions, setEffectivePermissions] = useState([]);
  const [creating, setCreating] = useState(false);
  const [deactivating, setDeactivating] = useState(null);
  
  const [newSubAdmin, setNewSubAdmin] = useState({
    user_email: "",
    user_name: "",
    role: "",
    scope: {
      all_agents: true,
      agent_ids: [],
      territory_ids: [],
      functions: []
    }
  });

  const organizationId = user?.org_id || "default-org";

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes, subAdminsRes] = await Promise.all([
        fetch(`${API_URL}/api/v2/rbac/roles`),
        fetch(`${API_URL}/api/v2/rbac/permissions`),
        fetch(`${API_URL}/api/v2/rbac/sub-admins?organization_id=${organizationId}`)
      ]);
      
      if (rolesRes.ok) {
        const data = await rolesRes.json();
        setRoles(data.roles.filter(r => r.assignable));
      }
      if (permsRes.ok) {
        const data = await permsRes.json();
        setPermissions(data.permissions);
      }
      if (subAdminsRes.ok) {
        const data = await subAdminsRes.json();
        setSubAdmins(data);
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load sub-admin data");
    } finally {
      setLoading(false);
    }
  };

  const createSubAdmin = async () => {
    if (!newSubAdmin.user_email || !newSubAdmin.user_name || !newSubAdmin.role) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/v2/rbac/sub-admins?assigned_by=${user.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          organization_id: organizationId,
          user_id: `user-${Date.now()}`,
          ...newSubAdmin
        })
      });
      
      if (!response.ok) throw new Error("Failed to create sub-admin");
      
      toast.success(`${newSubAdmin.user_name} added as ${ROLE_DESCRIPTIONS[newSubAdmin.role]?.name}`);
      setShowCreateDialog(false);
      setNewSubAdmin({
        user_email: "",
        user_name: "",
        role: "",
        scope: { all_agents: true, agent_ids: [], territory_ids: [], functions: [] }
      });
      loadData();
    } catch (error) {
      toast.error("Failed to create sub-admin");
    } finally {
      setCreating(false);
    }
  };

  const deactivateSubAdmin = async (subAdminId, userId) => {
    setDeactivating(subAdminId);
    try {
      const response = await fetch(
        `${API_URL}/api/v2/rbac/sub-admins/${userId}?organization_id=${organizationId}&deactivated_by=${user.id}`,
        { method: "DELETE" }
      );
      
      if (!response.ok) throw new Error("Failed to deactivate");
      
      toast.success("Sub-admin access revoked");
      loadData();
    } catch (error) {
      toast.error("Failed to revoke access");
    } finally {
      setDeactivating(null);
    }
  };

  const viewPermissions = async (subAdmin) => {
    setSelectedSubAdmin(subAdmin);
    try {
      const response = await fetch(
        `${API_URL}/api/v2/rbac/permissions/effective?user_id=${subAdmin.user_id}&organization_id=${organizationId}`
      );
      if (response.ok) {
        const data = await response.json();
        setEffectivePermissions(data.permissions);
      }
    } catch (error) {
      console.error("Failed to load permissions:", error);
    }
    setShowPermissionsDialog(true);
  };

  const getRoleBadge = (role) => {
    const roleInfo = ROLE_DESCRIPTIONS[role];
    if (!roleInfo) return <Badge variant="outline">{role}</Badge>;
    
    return (
      <Badge className={`${roleInfo.color} border-0`}>
        {roleInfo.name}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="sub-admins-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Sub-Admin Management</h1>
          <p className="text-muted-foreground">
            Delegate administrative access with role-based permissions
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="add-sub-admin-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Add Sub-Admin
        </Button>
      </div>

      {/* Role Overview */}
      <div className="grid md:grid-cols-4 gap-4">
        {Object.entries(ROLE_DESCRIPTIONS).slice(0, 4).map(([key, role]) => {
          const Icon = role.icon;
          const count = subAdmins.filter(sa => sa.role === key).length;
          
          return (
            <Card key={key} className="relative overflow-hidden">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className={`w-10 h-10 rounded-lg ${role.color} flex items-center justify-center`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="text-2xl font-bold">{count}</span>
                </div>
                <p className="mt-2 font-medium">{role.name}</p>
                <p className="text-sm text-muted-foreground">{role.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Sub-Admins List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Sub-Administrators ({subAdmins.length})
          </CardTitle>
          <CardDescription>
            Users with delegated administrative permissions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {subAdmins.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-semibold mb-2">No Sub-Admins Yet</h3>
              <p className="text-muted-foreground mb-4">
                Delegate administrative tasks by adding sub-admins with specific roles
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                Add Your First Sub-Admin
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subAdmins.map((subAdmin) => (
                  <TableRow key={subAdmin.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{subAdmin.user_name}</p>
                        <p className="text-sm text-muted-foreground">{subAdmin.user_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>{getRoleBadge(subAdmin.role)}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {subAdmin.scope?.all_agents ? (
                          <span className="text-emerald-600">All Agents</span>
                        ) : (
                          <span>{subAdmin.scope?.agent_ids?.length || 0} Agents</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(subAdmin.assigned_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewPermissions(subAdmin)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Permissions
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => deactivateSubAdmin(subAdmin.id, subAdmin.user_id)}
                          disabled={deactivating === subAdmin.id}
                        >
                          {deactivating === subAdmin.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4 mr-1" />
                              Revoke
                            </>
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Sub-Admin Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Add Sub-Administrator
            </DialogTitle>
            <DialogDescription>
              Assign administrative permissions to a team member
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  placeholder="John Smith"
                  value={newSubAdmin.user_name}
                  onChange={(e) => setNewSubAdmin({ ...newSubAdmin, user_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  placeholder="john@company.com"
                  value={newSubAdmin.user_email}
                  onChange={(e) => setNewSubAdmin({ ...newSubAdmin, user_email: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select
                value={newSubAdmin.role}
                onValueChange={(value) => setNewSubAdmin({ ...newSubAdmin, role: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => {
                    const roleInfo = ROLE_DESCRIPTIONS[role.role];
                    return (
                      <SelectItem key={role.role} value={role.role}>
                        <div className="flex items-center gap-2">
                          <span>{roleInfo?.name || role.name}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {newSubAdmin.role && ROLE_DESCRIPTIONS[newSubAdmin.role] && (
                <p className="text-sm text-muted-foreground">
                  {ROLE_DESCRIPTIONS[newSubAdmin.role].description}
                </p>
              )}
            </div>
            
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between">
                  <span>Advanced Scope Settings</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4 space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="all-agents"
                    checked={newSubAdmin.scope.all_agents}
                    onCheckedChange={(checked) => setNewSubAdmin({
                      ...newSubAdmin,
                      scope: { ...newSubAdmin.scope, all_agents: checked }
                    })}
                  />
                  <Label htmlFor="all-agents" className="text-sm font-normal">
                    Access to all agents
                  </Label>
                </div>
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Additional scope restrictions can be configured after creation
                  </AlertDescription>
                </Alert>
              </CollapsibleContent>
            </Collapsible>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={createSubAdmin} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Add Sub-Admin
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Permissions for {selectedSubAdmin?.user_name}
            </DialogTitle>
            <DialogDescription>
              {selectedSubAdmin && getRoleBadge(selectedSubAdmin.role)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 max-h-[400px] overflow-y-auto">
            <div className="grid grid-cols-2 gap-2">
              {effectivePermissions.map((permission) => (
                <div
                  key={permission}
                  className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 text-sm"
                >
                  <Check className="w-4 h-4 text-emerald-500" />
                  <span>{permission.replace(/_/g, " ")}</span>
                </div>
              ))}
            </div>
            
            {effectivePermissions.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                No permissions assigned
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowPermissionsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
